/**
 * SupportChatWidget — proactive AI receptionist "Noble" + live-agent handoff.
 *
 * External customer chat only. Visually + logically separate from any internal
 * Crew/Region chat used by field staff.
 *
 * Features (Feb 2026):
 *  - Role-aware quick-action buttons on open (Public / Resident / Staff)
 *  - "Tell Me More" drill-down after every AI answer
 *  - "Talk to a Human" surface after 3 assist clicks OR whenever escalation triggers
 *  - Round-robin handoff to online representative@nobleempirecs.com agents
 *  - WebSocket real-time with polling fallback
 *  - Post-chat resolution + 1-5 star rating
 *  - Lead-capture modal (Name / Email / Phone / Property Size / Message)
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { X, Send, Phone, Mail, User, Star, HelpCircle, MapPin, MessageCircle, Clock, ChevronDown, LoaderCircle, History } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { SupportEmailForm } from './SupportEmailForm';
import { SupportHandoffIntake } from './SupportHandoffIntake';
import { SupportConversation } from './SupportConversation';
import { SupportHistory } from './SupportHistory';
import { supportFetch as fetch, supportContext } from '../lib/supportTransport';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace(/^http/, 'ws');

const FALLBACK_CONTACTS = {
  company_name: 'Noble Empire Cleaning Services, LLC',
  email: 'support@nobleempirecs.com',
  phone: '+1 (888) 241-3077',
  address: '2450 Lakeside Pkwy, Ste 150, Flower Mound, TX 75022',
  hours: 'Mon–Fri, 8:30 AM–4:30 PM Central',
  phone_hours: 'Mon–Fri, 8:30 AM–4:30 PM Central',
};

export default function SupportChatWidget({ userRole = 'public' }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const canReadExecutiveWaitTime = user?.admin_department === 'executive' || user?.role === 'company_manager' || (user?.role === 'admin' && !user?.admin_department);
  const dockAboveNav = /^\/(resident|staff-app)(\/|$)/.test(pathname);
  const inlineLauncher = pathname === '/warehouse';
  const [open, setOpen] = useState(false);
  const dismissed = useRef(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hi! I'm Noble Empire Cleaning Services assistant. How can I help you?" },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [contacts, setContacts] = useState(FALLBACK_CONTACTS);
  const [escalate, setEscalate] = useState(false);
  const [agentJoined, setAgentJoined] = useState(false);
  const [agentName, setAgentName] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [assistClicks, setAssistClicks] = useState(0);
  const [offerHuman, setOfferHuman] = useState(false);
  const [leadForm, setLeadForm] = useState(null); // 'quote' | 'commercial' | null
  const [ratingPrompt, setRatingPrompt] = useState(false);
  const [wsReady, setWsReady] = useState(false);
  const [waitTimeLabel, setWaitTimeLabel] = useState(null);
  // Iter-97 · Noble AI Receptionist rebuild.
  const [proactive, setProactive] = useState([]);     // 5 buttons from backend
  const [offHours, setOffHours] = useState(false);
  const [visitorType, setVisitorType] = useState(null); // 'resident' | 'property_manager' | 'field_staff' | 'prospect'
  const [branchQuestions, setBranchQuestions] = useState([]);
  const [questionsOpen, setQuestionsOpen] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  // Iter-107 · Noble AI polish per CEO.
  // `proposalClicked` gates the "Tell me more" affordance so it only
  // appears AFTER the visitor has clicked a dedicated Noble proposal
  // (branch question). Once true, subsequent bot answers also show it.
  const [proposalClicked, setProposalClicked] = useState(false);
  // Iter-109 · Chat-vs-Call channel chooser shown after "Connect to Live
  // Agent" — holds the /channel-availability payload while open.
  const [channelChooser, setChannelChooser] = useState(null);
  const [channelAvailability, setChannelAvailability] = useState(null);
  const [handoffWaiting, setHandoffWaiting] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [quickStartOpen, setQuickStartOpen] = useState(true);
  const [conversationVisible, setConversationVisible] = useState(false);
  const [conversationStart, setConversationStart] = useState(0);
  const [chatEnded, setChatEnded] = useState(false);
  const hydratedRef = useRef(false);
  const activityRef = useRef(false);
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', business_type: '', message: '' });
  const scrollRef = useRef(null);
  const lastTsRef = useRef('');
  const wsRef = useRef(null);
  const pollStopRef = useRef(false);

  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [closing, setClosing] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const sessionRef = useRef(null);
  sessionRef.current = sessionId;

  const resetConversation = (sid = null) => {
    sessionRef.current = sid;
    lastTsRef.current = ''; hydratedRef.current = false; activityRef.current = false;
    setMessages([{ from: 'bot', text: "Hi! I'm Noble Empire Cleaning Services assistant. How can I help you?" }]);
    setSessionId(sid); setAgentJoined(false); setAgentName(null); setHandoffWaiting(false);
    setRatingPrompt(false); setChatEnded(false); setVisitorType(null); setBranchQuestions([]);
    setInput(''); setChannelChooser(null); setShowIntake(false); setProposalClicked(false);
    setQuickStartOpen(true);
    setConversationVisible(false); setConversationStart(0);
    setEscalate(false); setOfferHuman(false); setShowEmailForm(false); setShowQuoteForm(false);
    setLeadForm(null); setSending(false); setHandoffBusy(false); setAssistClicks(0);
    setQuestionsOpen(true); setShowHistory(false); setWsReady(false);
    setQuoteForm({ name: '', email: '', phone: '', business_type: '', message: '' });
  };

  const createConversation = async (previousId = null) => {
    await supportContext();
    const r = await fetch(`${API}/api/support-chat/${previousId ? `history/${previousId}/resume` : 'visitor/session'}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(previousId ? {} : { source_url: window.location.href }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Unable to start chat.');
    resetConversation(d.session_id);
    if (previousId) { setMessages(d.messages || []); setVisitorType(d.visitor_type || null); setShowIntake(true); setQuickStartOpen(false); setConversationVisible(true); }
    return d;
  };
  const newConversation = async () => {
    try { await createConversation(); } catch (e) { setSessionError(e.message); }
  };
  const closeWidget = async () => {
    if (closing) return;
    dismissed.current = true;
    setClosing(true); setSessionError('');
    try {
      if (sessionId) {
        const r = await fetch(`${API}/api/support-chat/session/${sessionId}/close`, { method: 'POST',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome: 'needs_review' }) });
        if (!r.ok) throw new Error('Your conversation could not be saved. Please try closing again.');
      }
      sessionRef.current = null; wsRef.current?.close();
      resetConversation(); setOpen(false);
    } catch (e) { setSessionError(e.message); }
    finally { setClosing(false); }
  };
  const reconnect = async previousId => {
    if (sessionId && !chatEnded) {
      const r = await fetch(`${API}/api/support-chat/session/${sessionId}/close`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome: 'needs_review' }) });
      if (!r.ok) throw new Error('Unable to save the current conversation. Please retry.');
    }
    await createConversation(previousId);
  };
  useEffect(() => {
    if (!open || sessionId) return;
    let active = true;
    setSessionError('');
    (async () => {
      try {
        await supportContext();
        const r = await fetch(`${API}/api/support-chat/visitor/session`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_url: window.location.href }) });
        const data = await r.json();
        if (!r.ok) throw new Error('Unable to start chat. Please reopen it.');
        if (active) setSessionId(data.session_id);
      } catch (e) { if (active) setSessionError(e.message); }
    })();
    return () => { active = false; };
  }, [open, sessionId]);

  // Preload contacts + role-aware suggestions + rolling avg wait time +
  // Iter-97 proactive buttons + off-hours flag.
  useEffect(() => {
    fetch(`${API}/api/support-chat/contacts`).then((r) => (r.ok ? r.json() : null)).then((d) => d && setContacts(d)).catch(() => {});
    fetch(`${API}/api/support-chat/suggestions?role=${encodeURIComponent(userRole)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSuggestions(d.buttons || []))
      .catch(() => {});
    if (canReadExecutiveWaitTime) fetch(`${API}/api/executive/chat-wait-time`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setWaitTimeLabel(d.average_label))
      .catch(() => {});
    fetch(`${API}/api/support-chat/proactive-buttons`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setProactive(d.buttons || []);
        setOffHours(Boolean(d.off_hours));
      })
      .catch(() => {});
  }, [userRole, canReadExecutiveWaitTime]);

  // Never cover editable dashboard controls with a proactive popup.
  useEffect(() => {
    if (userRole !== 'public') { setOpen(false); return; }
    const timer = setTimeout(() => { if (!dismissed.current) setOpen(true); }, 5000);
    return () => clearTimeout(timer);
  }, [userRole]);

  // Iter-84 · Listen for the "Request a Commercial Quote" CTA on the landing
  // page — it dispatches `necs:open-support-chat` with intent=commercial_quote,
  // which pops open the chat AND surfaces the commercial lead-capture form.
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const intent = e?.detail?.intent;
      if (intent === 'commercial_quote') setLeadForm('commercial');
      else if (intent === 'quote') setLeadForm('quote');
    };
    window.addEventListener('necs:open-support-chat', handler);
    return () => window.removeEventListener('necs:open-support-chat', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch(`${API}/api/support-chat/channel-availability`);
        if (response.ok && active) {
          const data = await response.json();
          setChannelAvailability(data);
          setChannelChooser(previous => previous ? data : null);
        }
      } catch { /* Action endpoints enforce current server hours. */ }
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [open]);

  const callOffice = async () => {
    try {
      const response = await fetch(`${API}/api/support-chat/channel-availability`);
      if (!response.ok) throw new Error('Availability unavailable');
      const data = await response.json();
      setChannelAvailability(data); setChannelChooser(data);
      if (data.call?.available) window.location.href = `tel:${data.phone.replace(/[^+\d]/g, '')}`;
    } catch {
      setMessages(m => [...m, { from: 'bot', text: 'Phone availability could not be confirmed. Please try again or email support.' }]);
    }
  };

  // Realtime — WebSocket first, polling fallback on error/disconnect
  useEffect(() => {
    if (!open || !sessionId) return undefined;
    let ws = null;
    let pollTimer = null;
    pollStopRef.current = false;
    let stopped = false;

    const applyState = (data) => {
      if (stopped) return;
      setAgentJoined(Boolean(data.agent_joined));
      setAgentName(data.agent_joined ? data.assigned_agent_name : null);
      setHandoffWaiting(!data.agent_joined && ['queued', 'notified', 'assigned'].includes(data.handoff_state));
      setChatEnded(data.status === 'closed' || data.handoff_state === 'awaiting_rating');
      setRatingPrompt((data.handoff_state === 'awaiting_rating' || data.feedback_requested) && !data.customer_rating);
      if (!hydratedRef.current && !activityRef.current) {
        if (data.messages?.length) setMessages(data.messages);
        if (data.visitor_type) setVisitorType(data.visitor_type);
        if (data.questions) setBranchQuestions(data.questions);
        if (data.messages?.length) setQuestionsOpen(false);
        hydratedRef.current = true;
        (data.messages || []).forEach(m => { if (m.ts > lastTsRef.current) lastTsRef.current = m.ts; });
      }
    };

    const applyIncoming = (m) => {
      if (stopped || !m || !m.ts) return;
      if (m.ts <= lastTsRef.current) return;
      lastTsRef.current = m.ts;

      if (m.kind === 'resolution_prompt') {
        setRatingPrompt(true);
        setAgentJoined(false); setHandoffWaiting(false); setChatEnded(true);
      }
      // Only display agent + system messages (user + bot echoes we already appended locally)
      if (m.from === 'agent' || m.from === 'system') {
        setMessages((cur) => [...cur, { from: m.from, text: m.text, agent_name: m.agent_name, kind: m.kind }]);
      }
      if (m.from === 'agent') {
        setAgentJoined(true);
        setEscalate(true);
        if (m.agent_name) setAgentName(m.agent_name);
      }
    };

    const startPolling = () => {
      const tick = async () => {
        if (stopped) return;
        try {
          const since = hydratedRef.current && lastTsRef.current ? `?since=${encodeURIComponent(lastTsRef.current)}` : '';
          const res = await fetch(`${API}/api/support-chat/session/${sessionId}/messages${since}`);
          if (res.ok) {
            const data = await res.json();
            applyState(data);
            (data.messages || []).forEach(applyIncoming);
          }
        } catch { /* ignore */ }
        if (!stopped) pollTimer = setTimeout(tick, 4000);
      };
      tick();
    };

    try {
      ws = new WebSocket(`${WS_URL}/api/support-chat/ws/${sessionId}`);
      wsRef.current = ws;
      ws.onopen = () => setWsReady(true);
      ws.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          if (payload.type === 'hello') {
            const initial = (payload.session && payload.session.messages) || [];
            applyState(payload.session || {});
            initial.forEach(applyIncoming);
          } else if (payload.type === 'message') {
            applyIncoming(payload.message);
          } else if (payload.type === 'agent_joined') {
            setAgentJoined(true);
            setHandoffWaiting(false);
            setEscalate(true);
            if (payload.agent_name) setAgentName(payload.agent_name);
          } else if (payload.type === 'resolution_prompt') {
            setRatingPrompt(true);
            setAgentJoined(false); setHandoffWaiting(false); setChatEnded(true);
          } else if (payload.type === 'session_closed') {
            setChatEnded(true); setHandoffWaiting(false); setAgentJoined(false); setAgentName(null);
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => {
        setWsReady(false);
      };
      ws.onclose = () => {
        setWsReady(false);
      };
    } catch { setWsReady(false); }
    // Reconcile state even with a healthy socket (multi-worker or missed events).
    startPolling();

    return () => {
      pollStopRef.current = true;
      stopped = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
      wsRef.current = null;
    };
  }, [open, sessionId]);

  // Keep the newest question and the start of its answer in view. Scrolling
  // to scrollHeight instead lands on the suggestion menus and hides the reply.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!open || !scroller || !messages.length) return;
    let index = messages.length - 1;
    if (messages[index].from === 'bot') {
      for (let i = index - 1; i >= 0; i -= 1) {
        if (messages[i].from === 'user') { index = i; break; }
      }
    }
    const target = scroller.querySelector(`[data-chat-index="${index}"]`);
    if (target) {
      scroller.scrollTop += target.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 12;
    }
  }, [messages, open]);

  // Explicitly requested forms/connection choices still open at the bottom.
  useLayoutEffect(() => {
    if (open && scrollRef.current && (channelChooser || showQuoteForm || showEmailForm || ratingPrompt || showIntake)) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [channelChooser, showQuoteForm, showEmailForm, ratingPrompt, showIntake, open]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending || chatEnded || !sessionId || closing) return;
    activityRef.current = true;
    if (!conversationVisible) setConversationStart(messages.length);
    setConversationVisible(true); setQuickStartOpen(false);
    setInput('');
    setQuestionsOpen(false);
    setMessages((m) => [...m, { from: 'user', text }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/api/support-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text, user_role: userRole }),
      });
      if (!res.ok) throw new Error('chat failed');
      const data = await res.json();
      if (sessionRef.current !== sessionId) return;
      if (data.reply) setMessages((m) => [...m, { from: 'bot', text: data.reply }]);
      if (data.escalate) setEscalate(true);
      if (data.has_agent) setAgentJoined(true);
      if (data.contacts) setContacts(data.contacts);
    } catch {
      if (sessionRef.current !== sessionId) return;
      setMessages((m) => [...m, { from: 'bot', text: "I couldn't connect just now. Please try again in a moment." }]);
      setEscalate(true);
    } finally {
      if (sessionRef.current === sessionId) setSending(false);
    }
  };

  // Iter-97 · Detect visitor type + load their branch questions.
  const detectVisitor = async (vt) => {
    if (sending || !sessionId || closing) return;
    setSessionError('');
    setQuickStartOpen(false);
    setConversationVisible(false);
    activityRef.current = true;
    setSending(true);
    setMessages((m) => [...m, { from: 'user', text: `[${vt.replace('_', ' ')}]` }]);
    try {
      const res = await fetch(`${API}/api/support-chat/visitor-type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, visitor_type: vt }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Unable to load topics. Please retry.');
      if (sessionRef.current !== sessionId) return;
      setVisitorType(vt);
      setBranchQuestions(d.questions || []);
      setQuestionsOpen(true);
      if (d.welcome) setMessages((m) => [...m, { from: 'bot', text: d.welcome, kind: 'branch_welcome' }]);
    } catch (error) {
      if (sessionRef.current !== sessionId) return;
      setSessionError(error.message); setQuickStartOpen(true);
    } finally {
      if (sessionRef.current === sessionId) setSending(false);
    }
  };

  const answerBranch = async (q) => {
    if (!visitorType || sending) return;
    setConversationStart(messages.length); setConversationVisible(true);
    activityRef.current = true;
    setSending(true);
    setQuestionsOpen(false);
    setMessages((m) => [...m, { from: 'user', text: q.label }]);
    try {
      const res = await fetch(`${API}/api/support-chat/branch-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, visitor_type: visitorType, key: q.key }),
      });
      if (!res.ok) throw new Error('branch answer failed');
      const d = await res.json();
      if (sessionRef.current !== sessionId) return;
      // Iter-107 · Enable "Tell me more" from this point onward — the
      // visitor has clicked a dedicated Noble AI proposal.
      setProposalClicked(true);
      setMessages((m) => [
        ...m,
        { from: 'bot', text: d.answer },
      ]);
    } catch {
      if (sessionRef.current !== sessionId) return;
      setMessages((m) => [...m, { from: 'bot', text: "I couldn't load that answer just now. Please try again." }]);
    } finally {
      if (sessionRef.current === sessionId) setSending(false);
    }
  };

  // Iter-97 · Proactive button click. Navigate / open quote form / branch.
  const clickProactive = async (btn) => {
    if (sending) return;
    if (btn.action === 'navigate' && btn.href) {
      window.location.href = btn.href;
      return;
    }
    if (btn.action === 'quote_form') {
      setShowQuoteForm(true);
      setVisitorType(btn.visitor_type);
      return;
    }
    // action === 'branch'
    await detectVisitor(btn.visitor_type);
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/support-chat/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, ...quoteForm, service_line: quoteForm.business_type }),
      });
      // Master Addendum · CRM Integration — mirror every SupportChatWidget
      // quote submission into the unified Noble AI Leads CRM so the Admin
      // dashboard shows a single funnel for phone / website / app leads.
      // Fires in parallel; failure here must NEVER block the primary chat
      // submission. `source` is "app" when the visitor is already
      // authenticated (userRole !== 'public'), else "website".
      try {
        const source = (userRole && userRole !== 'public') ? 'app' : 'website';
        await fetch(`${API}/api/leads/inbound-call`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source,
            caller_name: quoteForm.name,
            follow_up_email: quoteForm.email,
            caller_phone: quoteForm.phone,
            company_or_property_name: '',
            service_requested: quoteForm.business_type ? [quoteForm.business_type] : ['Support Chat Inquiry'],
            special_requirements: quoteForm.message || '',
            preferred_contact_method: quoteForm.email ? 'email' : (quoteForm.phone ? 'phone' : ''),
            status: 'New',
          }),
        });
      } catch { /* silent — chat flow is source of truth */ }
      if (res.ok) {
        setShowQuoteForm(false);
        setMessages((m) => [
          ...m,
          { from: 'user', text: `[Quote requested] ${quoteForm.name}` },
          { from: 'bot', text: "Got it. Your quote request just fired to our team. Expect a follow-up within one business day. Redirecting you to our full Contact Us form for anything else you'd like to add…" },
        ]);
        setTimeout(() => { window.location.href = `/contact?type=${encodeURIComponent(quoteForm.business_type || '')}`; }, 1400);
      }
    } catch { /* silent */ }
  };

  const clickSuggestion = async (btn) => {
    if (sending) return;
    setQuestionsOpen(false);
    if (btn.action === 'link' && btn.href) {
      window.location.href = btn.href;
      return;
    }
    if (btn.action === 'lead_form') {
      setLeadForm(btn.form);
      setMessages((m) => [...m, { from: 'user', text: `[Quick action] ${btn.label}` }]);
      return;
    }
    if (btn.action === 'handoff') {
      await openChannelChooser();
      return;
    }
    // faq_lookup — call the click endpoint
    setSending(true);
    setMessages((m) => [...m, { from: 'user', text: `[Quick action] ${btn.label}` }]);
    try {
      const res = await fetch(`${API}/api/support-chat/suggestion-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, key: btn.key }),
      });
      if (sessionRef.current !== sessionId) return;
      if (res.ok) {
        const d = await res.json();
        setMessages((m) => [
          ...m,
          { from: 'bot', text: d.answer || 'Let me look that up for you…' },
        ]);
        setAssistClicks(d.assist_click_count || 0);
        if (d.offer_human) setOfferHuman(true);
      } else {
        throw new Error('quick help failed');
      }
    } catch {
      if (sessionRef.current !== sessionId) return;
      setMessages((m) => [...m, { from: 'bot', text: "I couldn't load that answer just now. Please try again." }]);
    } finally {
      if (sessionRef.current === sessionId) setSending(false);
    }
  };

  const tellMeMore = async () => {
    if (sending) return;
    setSending(true);
    setQuestionsOpen(false);
    setMessages((m) => [...m, { from: 'user', text: '[Tell me more]' }]);
    try {
      const res = await fetch(`${API}/api/support-chat/drill-down`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, user_role: userRole }),
      });
      if (sessionRef.current !== sessionId) return;
      if (res.ok) {
        const d = await res.json();
        setMessages((m) => [...m, { from: 'bot', text: d.reply }]);
      } else {
        throw new Error('drill-down failed');
      }
    } catch {
      if (sessionRef.current !== sessionId) return;
      setMessages((m) => [...m, { from: 'bot', text: "I couldn't expand that answer just now. Please try again." }]);
    } finally {
      if (sessionRef.current === sessionId) setSending(false);
    }
  };

  // Iter-109 · "Connect to Live Agent" now opens the chat-vs-call chooser
  // with live wait estimates per channel instead of firing a handoff blind.
  const openChannelChooser = async () => {
    try {
      const res = await fetch(`${API}/api/support-chat/channel-availability`);
      if (res.ok) {
        const data = await res.json();
        setChannelAvailability(data); setShowEmailForm(false); setChannelChooser(data);
        return;
      }
    } catch { /* fall through */ }
    setChannelChooser({ chat: { available: false, wait_label: 'Unavailable', detail: 'Availability could not be checked. Please try again or email support.' }, call: { available: false, wait_label: 'Unavailable', detail: contacts.phone_hours }, phone: contacts.phone });
  };

  const triggerHandoff = async (reason) => {
    if (handoffBusy || handoffWaiting || agentJoined || chatEnded) return false;
    activityRef.current = true;
    setConversationVisible(true); setQuickStartOpen(false);
    setHandoffBusy(true);
    setMessages((m) => [...m, { from: 'user', text: '[Connect to live support]' }]);
    try {
      const res = await fetch(`${API}/api/support-chat/handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, user_role: userRole, reason }),
      });
      const d = await res.json();
      if (sessionRef.current !== sessionId) return false;
      if (res.ok) {
        setMessages((m) => [...m, { from: 'bot', text: d.message }]);
        if (d.connected && d.agent_name) setAgentName(d.agent_name);
        setHandoffWaiting(Boolean(d.waiting || d.assigned) && !d.connected);
        if (d.connected) setAgentJoined(true);
        setEscalate(true);
        return true;
      } else {
        setMessages(m => [...m, { from: 'bot', text: d.detail || 'Live contact is unavailable. You can email support instead.' }]);
      }
    } catch {
      if (sessionRef.current !== sessionId) return false;
      setMessages(m => [...m, { from: 'bot', text: 'The connection request could not be sent. Please try again or email support.' }]);
    } finally { if (sessionRef.current === sessionId) setHandoffBusy(false); }
    return false;
  };

  const lastBotIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].from === 'bot') return i;
    }
    return -1;
  })();

  return (
    <>
      {/* Floating action button */}
      {(!inlineLauncher || open) && <button
        type="button"
        onClick={() => open ? closeWidget() : setOpen(true)}
        disabled={closing}
        className={`fixed ${dockAboveNav ? 'bottom-24' : 'bottom-6'} right-4 sm:right-6 z-40 h-14 w-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110`}
        style={{ backgroundColor: '#000000', color: '#FFFFFF', border: '2px solid #000000' }}
        aria-label={open ? 'Close support chat' : 'Open support chat'}
        data-testid="support-chat-fab"
      >
        {open ? <X className="h-6 w-6" /> : (
          <svg className="h-7 w-7" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3 4.5 H21 V16.5 H8.5 L4.5 20.25 V16.5 H3 Z" fill="#FFFFFF" stroke="#FFFFFF" strokeWidth="0.75" strokeLinejoin="round" />
            <line x1="6.25" y1="8.25"  x2="17.75" y2="8.25"  stroke="#000000" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="6.25" y1="10.75" x2="17.75" y2="10.75" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="6.25" y1="13.25" x2="13.5"  y2="13.25" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
        {!open && (
          <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full" style={{ backgroundColor: '#10b981', border: '2px solid #000000' }} />
        )}
      </button>}

      {/* Chat panel */}
      {open && (
        <div
          className={`fixed ${dockAboveNav ? 'bottom-40' : 'bottom-24'} right-4 sm:right-6 z-40 w-[92vw] max-w-sm rounded-2xl shadow-2xl flex flex-col overflow-hidden`}
          style={{ backgroundColor: '#191b1e', border: '1px solid #C9A962', maxHeight: dockAboveNav ? 'calc(100dvh - 17rem)' : 'calc(100dvh - 13rem)' }}
          data-testid="support-chat-panel"
        >
          {/* Header */}
          <div className="px-4 py-4 flex items-center gap-3 shrink-0" style={{ backgroundColor: '#2b3034', borderBottom: '1px solid #45494b' }}>
            <div className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center" style={{ backgroundColor: '#C9A962', color: '#202326' }}>
              <User className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="support-chat-title">
                {agentJoined ? (agentName ? agentName.toUpperCase() : 'NOBLE EMPIRE SPECIALIST') : 'NOBLE — AI Support'}
              </p>
              <p className="text-[10px]" style={{ color: handoffWaiting ? '#C9A962' : '#10b981' }} data-testid="support-chat-connection-status">
                {(handoffBusy || handoffWaiting) && !agentJoined ? <LoaderCircle className="inline-block w-3 h-3 mr-1 animate-spin" aria-label="Searching for an agent" data-testid="support-chat-connection-spinner"/> : '● '} {agentJoined ? `Connected to ${agentName || 'your agent'}` : chatEnded ? 'Conversation ended' : (handoffWaiting || handoffBusy) ? 'Searching for an available agent…' : 'AI online · Replies in seconds'}
                {wsReady && <span className="ml-2 text-zinc-500">· realtime</span>}
              </p>
            </div>
            <button type="button" title="Previous conversations" aria-label="Previous conversations" aria-pressed={showHistory} onClick={() => setShowHistory(value => !value)} disabled={!sessionId || closing} className="shrink-0 text-[#C9A962] hover:text-white transition-colors disabled:opacity-50" data-testid="support-chat-previous-conversations"><History className="h-5 w-5" /></button>
            <button onClick={closeWidget} disabled={closing} aria-label="Save and close conversation" className="shrink-0 text-zinc-400 hover:text-white disabled:opacity-50" data-testid="support-chat-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3" style={{ overflowAnchor: 'none' }} data-testid="support-chat-messages">
            {sessionError && <p role="alert" className="text-red-300 text-sm" data-testid="support-chat-session-error">{sessionError}</p>}
            {closing && <p role="status" data-testid="support-chat-saving">Saving conversation…</p>}
            {showHistory ? <SupportHistory onBack={() => setShowHistory(false)} onResume={reconnect} /> : <>
            {quickStartOpen && !agentJoined && !handoffWaiting && <p className="text-sm leading-relaxed text-zinc-100 rounded-2xl rounded-bl-sm px-4 py-3 bg-[#2b3034] border border-[#45494b]" data-testid="support-chat-introduction">Hi! I'm Noble Empire Cleaning Services assistant. How can I help you?</p>}
            {!sessionId && !sessionError && <p role="status" className="text-xs text-zinc-400" data-testid="support-chat-initializing">Starting your conversation…</p>}

            {/* Suggestions follow the latest exchange, never a pinned welcome. */}
                {!chatEnded && !agentJoined && !showQuoteForm && quickStartOpen && proactive.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="support-chat-proposals-inline">
                    <div className="space-y-2" data-testid="support-chat-proactive-buttons">
                      <p className="text-xs uppercase text-[#C9A962] font-bold mb-2" data-testid="support-chat-quick-start-title">Quick Start</p>
                      {proactive.map((btn) => (
                        <button
                          key={btn.key}
                          type="button"
                          onClick={() => clickProactive(btn)}
                          disabled={sending || !sessionId}
                          className="w-full min-h-9 text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-[#353a3e] bg-[#24282c] disabled:opacity-50"
                          style={{ color: '#f4f4f5', border: '1px solid #C9A962' }}
                          data-testid={`support-chat-proactive-${btn.key}`}
                        >
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
            {/* Compact follow-ups stay below each answer, without pushing it out of view. */}
            {visitorType && !quickStartOpen && !chatEnded && !agentJoined && !showQuoteForm && (
              <div className="space-y-2" data-testid="support-chat-branch-bottom">
                <button type="button" onClick={() => { setQuickStartOpen(true); setConversationVisible(false); }} disabled={sending}
                  className="text-xs text-[#C9A962] py-2" data-testid="support-chat-back-quick-start">← Quick Start</button>
                {branchQuestions.length > 0 && (
                  <div className="space-y-1.5" data-testid={`support-chat-branch-${visitorType}`}>
                    <button
                      type="button"
                      onClick={() => setQuestionsOpen((value) => !value)}
                      disabled={sending}
                      aria-expanded={questionsOpen}
                      aria-controls="support-chat-common-question-options"
                      className="w-full flex items-center justify-between py-1 text-[10px] uppercase tracking-widest text-[#C9A962] font-bold disabled:opacity-50"
                      data-testid="support-chat-common-questions-toggle"
                    >
                      Common questions
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${questionsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>
                    {questionsOpen && <div id="support-chat-common-question-options" className="space-y-1.5" data-testid="support-chat-common-question-options">{branchQuestions.map((q) => (
                      <button
                        key={q.key}
                        type="button"
                        onClick={() => answerBranch(q)}
                        disabled={sending}
                        className="w-full text-left px-3 py-1.5 rounded-lg text-xs transition"
                        style={{ backgroundColor: 'rgba(201,169,98,0.08)', color: '#e6e6e6', border: '1px dashed #C9A962' }}
                        data-testid={`support-chat-branch-q-${q.key}`}
                      >
                        {q.label}
                      </button>
                    ))}</div>}
                  </div>
                )}
                {suggestions.length > 0 && (
                  <div data-testid="support-chat-suggestions">
                    <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5">Quick help</p>
                    <div className="flex flex-wrap gap-1.5">
                      {suggestions.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => clickSuggestion(s)}
                          disabled={sending}
                          className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full transition"
                          style={{ backgroundColor: '#2D2D2D', color: '#C9A962', border: '1px solid #C9A962' }}
                          data-testid={`support-chat-suggestion-${s.key}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {((!quickStartOpen && conversationVisible) || agentJoined || handoffWaiting || chatEnded) && <SupportConversation messages={messages}
              start={agentJoined || chatEnded ? 0 : conversationStart} lastBotIndex={lastBotIndex} onMore={tellMeMore}
              allowMore={!chatEnded && !agentJoined && !sending && proposalClicked}/>}
            {quickStartOpen && !agentJoined && !handoffWaiting && messages.length > 1 && <button type="button" className="text-xs text-zinc-400 underline" onClick={()=>{setQuickStartOpen(false);setConversationStart(0);setConversationVisible(true);}} data-testid="support-chat-show-history">View current conversation</button>}
            {sending && <p className="text-xs text-zinc-400" role="status" data-testid="support-chat-typing">Noble is typing…</p>}

            {/* Iter-109 · Chat-vs-Call channel chooser — appears after the
                visitor taps "Connect to Live Agent". Shows live wait
                estimates per channel; off-hours raises a professional
                notice card instead of a plain text strip. */}
            {channelChooser && !agentJoined && (
              <div
                className="rounded-xl p-3 space-y-2"
                style={{ backgroundColor: '#2D2D2D', border: '1px solid #C9A962' }}
                data-testid="support-chat-channel-chooser"
              >
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold">
                    How would you like to connect?
                  </p>
                  <button
                    type="button"
                    onClick={() => setChannelChooser(null)}
                    className="text-zinc-400 hover:text-white"
                    data-testid="support-chat-channel-close"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => { setChannelChooser(null); setShowIntake(true); }}
                  disabled={!channelChooser.chat?.available || handoffBusy || handoffWaiting}
                  className="w-full text-left p-3 rounded-lg transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#1f1f1f', border: '1px solid #C9A962' }}
                  data-testid="support-chat-channel-chat"
                >
                  <div className="flex items-center gap-2 text-xs font-bold text-white">
                    <MessageCircle className="h-3.5 w-3.5 text-[#C9A962]" />
                    Connect to a live agent
                    <span className="ml-auto text-[10px] font-semibold" style={{ color: channelChooser.chat?.available ? '#10b981' : '#f87171' }}>
                      {channelChooser.chat?.wait_label}
                    </span>
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1 leading-relaxed">{channelChooser.chat?.detail}</p>
                </button>

                {channelChooser.call?.available ? (
                  <button
                    type="button"
                    onClick={callOffice}
                    className="block w-full text-left p-3 rounded-lg transition hover:opacity-90"
                    style={{ backgroundColor: '#1f1f1f', border: '1px solid #C9A962' }}
                    data-testid="support-chat-channel-call"
                  >
                    <div className="flex items-center gap-2 text-xs font-bold text-white">
                      <Phone className="h-3.5 w-3.5 text-[#C9A962]" />
                      Phone Call
                      <span className="ml-auto text-[10px] font-semibold" style={{ color: '#10b981' }}>
                        {channelChooser.call?.wait_label}
                      </span>
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1 leading-relaxed">{channelChooser.call?.detail}</p>
                  </button>
                ) : (
                  <div
                    className="w-full text-left p-3 rounded-lg opacity-80"
                    style={{ backgroundColor: '#1f1f1f', border: '1px dashed #6b6b6b' }}
                    data-testid="support-chat-channel-call-closed"
                  >
                    <div className="flex items-center gap-2 text-xs font-bold text-zinc-400">
                      <Phone className="h-3.5 w-3.5" />
                      Phone Call
                      <span className="ml-auto text-[10px] font-semibold text-red-400">
                        {channelChooser.call?.wait_label}
                      </span>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{channelChooser.call?.detail}</p>
                  </div>
                )}

                {channelChooser.off_hours && (
                  <div
                    className="rounded-lg p-3 flex gap-2.5"
                    style={{
                      background: 'linear-gradient(135deg, rgba(201,169,98,0.16) 0%, rgba(201,169,98,0.06) 100%)',
                      borderLeft: '3px solid #C9A962',
                      border: '1px solid rgba(201,169,98,0.45)',
                    }}
                    data-testid="support-chat-offhours-card"
                  >
                    <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#C9A962' }} />
                    <div>
                      <p className="text-[11px] font-bold text-white uppercase tracking-wider mb-1">Outside Business Hours</p>
                      <p className="text-[11px] leading-relaxed" style={{ color: '#e5e5e5' }}>
                        Live chat and phone are inactive outside Monday–Friday, 8:30 AM–4:30 PM Central.
                        Noble AI and email support remain available. Phone: (888) 241-3077.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showIntake && !agentJoined && !chatEnded && <SupportHandoffIntake sessionId={sessionId} visitorType={visitorType}
              onCancel={() => setShowIntake(false)} onConnect={async (details) => {
                setVisitorType(details.visitor_type);
                setBranchQuestions(details.questions || []);
                const connected = await triggerHandoff(`Customer chose live chat: ${details.topic_label}`);
                if (connected) setShowIntake(false);
                return connected;
              }} />}
            {chatEnded && <Button type="button" onClick={newConversation} className="bg-[#C9A962] text-black" data-testid="support-chat-new-conversation">Start a new chat</Button>}

            {/* Iter-103 · Item 4 — Proposals lifted to `support-chat-proposals-top`
                above the scroll. Inline blocks below are retained but disabled
                (false &&) so any legacy tests referencing them still find the
                selectors while the visible rendering happens above. */}
            {false && !visitorType && !agentJoined && proactive.length > 0 && !showQuoteForm && (
              <div className="pt-2 space-y-1.5" data-testid="support-chat-proactive-buttons">
                <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold">Quick start</p>
                {proactive.map((btn) => (
                  <button
                    key={btn.key}
                    type="button"
                    onClick={() => clickProactive(btn)}
                    className="w-full text-left px-3 py-1.5 rounded-lg text-xs transition"
                    style={{ backgroundColor: '#2D2D2D', color: '#e6e6e6', border: '1px solid #C9A962' }}
                    data-testid={`support-chat-proactive-${btn.key}`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            )}

            {/* Iter-97 · Visitor type selector — 4 buttons if no proactive click yet. */}
            {false && !visitorType && !agentJoined && proactive.length === 0 && !showQuoteForm && (
              <div className="pt-2 space-y-1.5" data-testid="support-chat-visitor-selector">
                {[
                  { key: 'resident', label: 'Resident / Tenant' },
                  { key: 'property_manager', label: 'Property Manager / Commercial Client' },
                  { key: 'field_staff', label: 'Field Staff Member' },
                  { key: 'prospect', label: 'Prospective Client' },
                ].map((v) => (
                  <button key={v.key} type="button" onClick={() => detectVisitor(v.key)}
                          className="w-full text-left px-3 py-1.5 rounded-lg text-xs transition"
                          style={{ backgroundColor: '#2D2D2D', color: '#e6e6e6', border: '1px solid #C9A962' }}
                          data-testid={`support-chat-visitor-${v.key}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            )}

            {/* Iter-97 · Branch question pills after visitor type detected. */}
            {false && visitorType && !agentJoined && branchQuestions.length > 0 && !showQuoteForm && (
              <div className="pt-2 space-y-1.5" data-testid={`support-chat-branch-${visitorType}`}>
                <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold">Common questions</p>
                {branchQuestions.map((q) => (
                  <button key={q.key} type="button" onClick={() => answerBranch(q)}
                          className="w-full text-left px-3 py-1.5 rounded-lg text-xs transition"
                          style={{ backgroundColor: 'rgba(201,169,98,0.08)', color: '#e6e6e6', border: '1px dashed #C9A962' }}
                          data-testid={`support-chat-branch-q-${q.key}`}>
                    {q.label}
                  </button>
                ))}
              </div>
            )}

            {/* Iter-97 · Inline quote request form. */}
            {showQuoteForm && (
              <form onSubmit={submitQuote}
                    className="p-3 rounded-lg mt-2 space-y-2"
                    style={{ backgroundColor: '#2D2D2D', border: '1px solid #C9A962' }}
                    data-testid="support-chat-quote-form">
                <p className="text-xs font-bold text-[#C9A962] uppercase tracking-widest">Quote Request</p>
                <input required placeholder="Your name" value={quoteForm.name}
                       onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                       className="w-full text-xs px-2 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-white"
                       data-testid="support-chat-quote-name" />
                <input required type="email" placeholder="Email" value={quoteForm.email}
                       onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                       className="w-full text-xs px-2 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-white"
                       data-testid="support-chat-quote-email" />
                <input placeholder="Phone" value={quoteForm.phone}
                       onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                       className="w-full text-xs px-2 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-white"
                       data-testid="support-chat-quote-phone" />
                <select required value={quoteForm.business_type}
                        onChange={(e) => setQuoteForm({ ...quoteForm, business_type: e.target.value })}
                        className="w-full text-xs px-2 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-white"
                        data-testid="support-chat-quote-type">
                  <option value="">Business type…</option>
                  <option value="NECS Valet">NECS Valet — Residential Community</option>
                  <option value="NECS Office Pro">NECS Office Pro — Office / Corporate</option>
                  <option value="NECS Retail Clean">NECS Retail Clean — Retail Plaza</option>
                  <option value="NECS Station Care">NECS Station Care — Gas Station</option>
                  <option value="NECS Campus Clean">NECS Campus Clean — School</option>
                  <option value="NECS MedSafe">NECS MedSafe — Medical Clinic</option>
                  <option value="NECS Industrial">NECS Industrial — Warehouse</option>
                  <option value="NECS DineClean">NECS DineClean — Restaurant</option>
                  <option value="NECS MallCare">NECS MallCare — Mall</option>
                  <option value="NECS Custom">NECS Custom — Other</option>
                </select>
                <textarea rows={2} placeholder="Anything else we should know?" value={quoteForm.message}
                          onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                          className="w-full text-xs px-2 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-white" />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowQuoteForm(false)}
                          className="text-[10px] text-zinc-400 hover:text-white">Cancel</button>
                  <button type="submit"
                          className="flex-1 py-1.5 rounded text-[11px] font-black uppercase tracking-widest"
                          style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                          data-testid="support-chat-quote-submit">
                    Send Quote Request
                  </button>
                </div>
              </form>
            )}

            {/* Iter-100 · Item 7 — Quick Help hidden until the visitor makes
                a Quick Start selection (visitorType) or the CS agent joins.
                Prevents the "Quick help" chip strip from crowding the
                first-load Quick Start experience. */}
            {false && !agentJoined && suggestions.length > 0 && visitorType && (
              <div className="pt-2" data-testid="support-chat-suggestions">
                <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5">Quick help</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => clickSuggestion(s)}
                      className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full transition"
                      style={{ backgroundColor: '#2D2D2D', color: '#C9A962', border: '1px solid #C9A962' }}
                      data-testid={`support-chat-suggestion-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showEmailForm && <SupportEmailForm sessionId={sessionId} onCancel={() => setShowEmailForm(false)} onSent={() => {
              setShowEmailForm(false);
              setMessages(m => [...m, { from: 'bot', text: 'Your email was sent to support@nobleempirecs.com. Support will reply to the email address you provided.' }]);
            }} />}

            {/* Post-chat rating */}
            {ratingPrompt && (
              <RatingBlock
                sessionId={sessionId}
                agentName={agentName}
                onSubmitted={() => {
                  setRatingPrompt(false);
                  setMessages((m) => [...m, { from: 'bot', text: 'Thanks for your feedback — we really appreciate it!' }]);
                }}
              />
            )}
            </>}
          </div>

          {/* Iter-109 · Escalation bar — Connect to Live Agent (opens the
              chat-vs-call chooser) + Email. The standalone Call pill was
              removed; the call option now lives inside the chooser. */}
          {!showHistory && !chatEnded && !ratingPrompt && !agentJoined && (
            <div className="px-3 py-2 flex flex-wrap gap-2 items-center justify-center" style={{ backgroundColor: '#3a2a1a', borderTop: '1px solid #3D3D3D' }}>
              <button
                type="button"
                onClick={openChannelChooser}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
                style={{ backgroundColor: '#ffffff', color: '#C9A962', border: '1px solid #C9A962' }}
                data-testid="support-chat-talk-human"
              >
                <MapPin className="h-3 w-3" /> {channelAvailability?.off_hours ? 'Live support inactive' : handoffWaiting ? 'Searching for an agent…' : 'Connect to Live Agent'}
              </button>
              <button type="button" onClick={() => { setChannelChooser(null); setShowQuoteForm(false); setShowEmailForm(true); }}
                 className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
                 style={{ backgroundColor: '#ffffff', color: '#C9A962', border: '1px solid #C9A962' }}
                 data-testid="support-chat-email">
                <Mail className="h-3 w-3" /> Email
              </button>
            </div>
          )}

          {/* Composer */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="px-3 py-3 flex gap-2"
                style={{ backgroundColor: '#1f1f1f', borderTop: '1px solid #3D3D3D' }}>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={chatEnded ? 'This conversation has ended.' : 'Type your question…'}
              className="flex-1 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 focus-visible:ring-[#C9A962]"
              disabled={sending || chatEnded || !sessionId || showHistory || closing}
              data-testid="support-chat-input"
              maxLength={2000}
            />
            <Button
              type="submit"
              disabled={!input.trim() || sending || chatEnded || !sessionId || showHistory || closing}
              className="font-bold"
              style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
              data-testid="support-chat-send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}

      {/* Lead-capture modal */}
      {leadForm && (
        <LeadCaptureModal
          sessionId={sessionId}
          formType={leadForm}
          onClose={() => setLeadForm(null)}
          onSubmitted={(lead) => {
            setLeadForm(null);
            setMessages((m) => [...m, {
              from: 'bot',
              text: `Thanks, ${lead.name}! Your ${leadForm === 'quote' ? 'quote request' : 'commercial inquiry'} is in — a Noble Empire specialist will follow up within one business day.`,
            }]);
          }}
        />
      )}
    </>
  );
}

/** Post-chat resolution + 1-5 star rating capture. */
function RatingBlock({ sessionId, agentName, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [resolved, setResolved] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (submitting || resolved === null || stars === 0) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API}/api/support-chat/rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, stars, resolved, comment }),
      });
      if (!response.ok) throw new Error('Feedback could not be saved. Please retry.');
      onSubmitted();
    } catch { setError('Feedback could not be saved. Your selections are still here; please retry.'); } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl p-3 mt-1"
         style={{ backgroundColor: '#2D2D2D', border: '1px solid #C9A962' }}
         data-testid="support-chat-rating">
      <p className="text-xs text-white font-bold mb-2">
        Did we resolve your issue{agentName ? ` with ${agentName}` : ''} today?
      </p>
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setResolved(true)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${resolved === true ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-300 border border-zinc-600'}`}
          data-testid="support-chat-rating-yes"
        >
          ✅ Yes
        </button>
        <button
          type="button"
          onClick={() => setResolved(false)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${resolved === false ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-300 border border-zinc-600'}`}
          data-testid="support-chat-rating-no"
        >
          ❌ No
        </button>
      </div>
      <p className="text-[11px] text-zinc-400 mb-1">Rate your experience:</p>
      <div className="flex gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setStars(n)}
            className="p-1"
            data-testid={`support-chat-star-${n}`}
          >
            <Star className={`h-5 w-5 ${n <= stars ? 'text-[#FFD60A] fill-[#FFD60A]' : 'text-zinc-600'}`} />
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        placeholder="Optional comment…"
        className="w-full text-xs bg-zinc-900 border border-zinc-700 rounded p-2 text-white placeholder:text-zinc-500"
        maxLength={500}
        data-testid="support-chat-rating-comment"
      />
      <Button
        type="button"
        onClick={submit}
        disabled={submitting || resolved === null || stars === 0}
        className="w-full mt-2 font-bold text-xs"
        style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
        data-testid="support-chat-rating-submit"
      >
        {submitting ? 'Submitting…' : 'Submit Feedback'}
      </Button>
      {error && <p role="alert" className="mt-2 text-xs text-red-300" data-testid="support-chat-rating-error">{error}</p>}
    </div>
  );
}

/** Lead capture modal — Name / Email / Phone / Property Size / Message. */
function LeadCaptureModal({ sessionId, formType, onClose, onSubmitted }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', property_size: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/support-chat/lead-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, form_type: formType, ...form }),
      });
      // Master Addendum · CRM Integration — mirror the modal capture into
      // /api/leads/inbound-call so it lands in the unified Noble AI CRM.
      try {
        await fetch(`${API}/api/leads/inbound-call`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'website',
            caller_name: form.name,
            follow_up_email: form.email,
            caller_phone: form.phone,
            company_or_property_name: '',
            service_requested: [formType === 'quote' ? 'Quote Request' : 'Commercial Inquiry'],
            special_requirements: [form.property_size, form.message].filter(Boolean).join(' | '),
            preferred_contact_method: 'email',
            status: 'New',
          }),
        });
      } catch { /* silent */ }
      if (res.ok) {
        const d = await res.json();
        onSubmitted(d.lead);
      } else {
        const t = await res.json().catch(() => ({}));
        setErr(t.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = formType === 'quote' ? 'Request a Quote' : 'Commercial Inquiry';
  const subtitle = formType === 'quote'
    ? 'Share a few details and a Noble Empire specialist will send a written proposal within one business day.'
    : "Tell us about your commercial property and we'll route you to our Sales team.";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" data-testid="lead-capture-modal">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl overflow-hidden" style={{ backgroundColor: '#1f1f1f', border: '2px solid #C9A962' }}>
        <div className="px-5 py-4 flex items-start justify-between" style={{ backgroundColor: '#2D2D2D', borderBottom: '1px solid #3D3D3D' }}>
          <div>
            <p className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{title}</p>
            <p className="text-[11px] text-zinc-400 mt-1">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="lead-capture-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <FieldRow label="Full name">
            <Input required value={form.name} onChange={(e) => update('name', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="lead-capture-name" />
          </FieldRow>
          <FieldRow label="Email">
            <Input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="lead-capture-email" />
          </FieldRow>
          <FieldRow label="Phone">
            <Input required value={form.phone} onChange={(e) => update('phone', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="lead-capture-phone" />
          </FieldRow>
          <FieldRow label="Property size (units / sq ft / description)">
            <Input required value={form.property_size} onChange={(e) => update('property_size', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="lead-capture-property-size" />
          </FieldRow>
          <FieldRow label="Message">
            <textarea required value={form.message} onChange={(e) => update('message', e.target.value)} rows={3} maxLength={2000}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded p-2 text-white text-sm"
                      data-testid="lead-capture-message" />
          </FieldRow>
          {err && <p className="text-xs text-red-400" data-testid="lead-capture-err">{err}</p>}
          <Button type="submit" disabled={submitting} className="w-full font-bold"
                  style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                  data-testid="lead-capture-submit">
            {submitting ? 'Sending…' : 'Send Request'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{label}</p>
      {children}
    </div>
  );
}
